import { useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { meApi } from '../../api/endpoints/me';
import { handleApiError } from '../../api/errorMapping';
import { SecurityPageSection } from '../layouts/SecurityPageSection';
import { Button, Input, Loading } from '@fiado/react-kit';

/**
 * Self-service: usuario autenticado configura una app autenticadora (EMAIL → TOTP).
 * Flujo: enroll-totp (al montar) → mostrar QR + recovery codes → verificar código de 6 dígitos.
 * NO comparte estado con el wizard de primer-login (MfaTotpWizard).
 */

type Stage = 'INIT' | 'SHOW_QR' | 'ERROR';

interface EnrollData {
    qrDataUrl: string;
    recoveryCodes: string[];
    enrollmentToken: string;
}

export function MfaTotpSetup() {
    const qc = useQueryClient();
    const navigate = useNavigate();

    const [stage, setStage] = useState<Stage>('INIT');
    const [enroll, setEnroll] = useState<EnrollData | null>(null);
    const [totpCode, setTotpCode] = useState('');
    // Guard contra el doble-invoke de StrictMode (mismo patrón que MfaEmailSwitch.sentRef):
    // el enroll-totp DEBE dispararse una sola vez por entrada al stage INIT. El botón
    // "Reintentar" lo rearma explícitamente (ver onClick) para permitir un reintento real.
    const sentRef = useRef(false);

    const enrollMut = useMutation({
        mutationFn: () => meApi.enrollTotp(),
        onSuccess: (resp) => {
            setEnroll({
                qrDataUrl: resp.qrDataUrl,
                recoveryCodes: resp.recoveryCodes,
                enrollmentToken: resp.enrollmentToken,
            });
            setStage('SHOW_QR');
        },
        onError: (err) => {
            // Sin esto el stage queda en 'INIT' → spinner infinito.
            handleApiError(err);
            setStage('ERROR');
        },
    });

    const verifyMut = useMutation({
        mutationFn: () => {
            if (!enroll) throw new Error('enrollmentToken requerido para verificar');
            return meApi.verifyTotpEnrollment({
                totpCode,
                enrollmentToken: enroll.enrollmentToken,
            });
        },
        onSuccess: async () => {
            toast.success('Aplicación de autenticación configurada');
            await qc.invalidateQueries({ queryKey: ['me', 'security', 'mfa'] });
            void navigate({ to: '/me/profile' });
        },
        onError: handleApiError,
    });

    useEffect(() => {
        if (stage !== 'INIT') return;
        if (sentRef.current) return;
        sentRef.current = true;
        enrollMut.mutate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stage]);

    if (stage === 'ERROR')
        return (
            <SecurityPageSection title="Aplicación de autenticación" centered>
                <div className="max-w-md space-y-4">
                    <p className="text-sm text-muted-foreground">
                        No pudimos iniciar la configuración del autenticador. Vuelve a intentarlo en
                        unos segundos.
                    </p>
                    <div className="flex gap-3">
                        <Button
                            variant="outline"
                            onClick={() => {
                                void navigate({ to: '/me/profile' });
                            }}
                        >
                            Volver
                        </Button>
                        <Button
                            onClick={() => {
                                // Rearmar el guard: reintento explícito del usuario → permitimos un
                                // nuevo enroll-totp (el effect dispara al volver a stage INIT).
                                sentRef.current = false;
                                setStage('INIT');
                            }}
                        >
                            Reintentar
                        </Button>
                    </div>
                </div>
            </SecurityPageSection>
        );

    // El gate del spinner es el STAGE + enroll (que onSuccess setea de forma confiable), NO
    // enrollMut.isPending: bajo StrictMode el observer de la mutation se desincroniza en el
    // doble-mount y deja isPending pegado en true aunque onSuccess ya corrió → spinner eterno.
    if (stage === 'INIT' || !enroll)
        return (
            <SecurityPageSection title="Aplicación de autenticación" centered>
                <Loading text="Generando código…" />
            </SecurityPageSection>
        );

    return (
        <SecurityPageSection title="Aplicación de autenticación" centered>
            <p className="max-w-md text-sm text-muted-foreground">
                Escanea este código QR con tu app (Google Authenticator, Authy, etc.).
            </p>
            <div className="max-w-md space-y-4">
                <div className="flex justify-center">
                    <img
                        src={enroll.qrDataUrl}
                        alt="Código QR para la app de autenticación"
                        width={200}
                        height={200}
                        className="rounded-md border"
                    />
                </div>

                <div className="space-y-2 rounded-lg border border-warning/40 bg-warning/10 p-4">
                    <p className="text-sm font-medium text-warning">Códigos de recuperación</p>
                    <p className="text-xs text-warning">
                        Guarda estos códigos en un lugar seguro. Se muestran una sola vez y te
                        permiten recuperar el acceso si pierdes tu app.
                    </p>
                    <ul className="grid grid-cols-2 gap-1 font-mono text-sm text-warning">
                        {enroll.recoveryCodes.map((rc) => (
                            <li key={rc}>{rc}</li>
                        ))}
                    </ul>
                </div>

                <Input
                    id="totp-setup-code"
                    label="Ingresa el código de 6 dígitos de tu app"
                    inputMode="numeric"
                    maxLength={6}
                    value={totpCode}
                    onChange={(e) => {
                        setTotpCode(e.target.value);
                    }}
                />
                <Button
                    className="w-full"
                    onClick={() => {
                        verifyMut.mutate();
                    }}
                    disabled={totpCode.length !== 6 || verifyMut.isPending}
                >
                    Verificar y activar
                </Button>
            </div>
        </SecurityPageSection>
    );
}
